import { Map, List, Set } from 'immutable';
import moment from 'moment';

import trainTime from '../lib/TrainTime';
import BaseStore from './BaseStore';
import CookieHelper from '../helpers/Cookie';
import { ActionTypes, SmartcardStatus, CookieKeys } from '../constants/AppConstants';
import debugHook from '../hooks/hookUtils';
import useStoreState from './useStoreState';

class SmartcardStore extends BaseStore {

    getInitialState () {
        return new Map({
            lastUpdated: undefined,
            smartcard: undefined,
            smartcardDirty: false,
            smartcardFailure: null,
            smartcardFailureNotified: false,
            smartcards: new List(),
            activatedSmartcards: this.activatedSmartcardsCookie,
            associatedAccounts: new List(),
            associatedSmartcards: new List(),
            smartcardToAssociate: null,
            requestSummary: null,
        });
    }

    getActionHandlers () {
        return {
            [ ActionTypes.SMARTCARD_LOADED ]: this.handleSmartcardLoaded,
            [ ActionTypes.SMARTCARD_FAILED ]: this.handleSmartcardFailed,
            [ ActionTypes.SMARTCARD_FAILURE_NOTIFIED ]: this.handleSmartcardFailureNotified,
            [ ActionTypes.SMARTCARD_DIRTY ]: this.handleDirtySmartcard,
            [ ActionTypes.SMARTCARDS_LOADED ]: this.handlePropertyChange('smartcards'),
            [ ActionTypes.SMARTCARD_ACTIVATION_STARTED ]: this.handleSmartcardActivationStarted,
            [ ActionTypes.SMARTCARD_ACTIVATED ]: this.handleManualActivation,
            [ ActionTypes.SMARTCARD_ACTIVATION_FAILED ]: this.handleSmartcardActivationFailure,
            [ ActionTypes.SMARTCARD_PAPER_TRANSFER_COMPLETE ]: this.handlePaperTransfer,
            [ ActionTypes.SMARTCARD_ASSOCIATED_ACCOUNTS_LOADED ]: this.handleAssociatedAccountsLoaded,
            [ ActionTypes.SMARTCARD_ASSOCIATED_SMARTCARDS_LOADED ]: this.handleAssociatedSmartcardsLoaded,
            [ ActionTypes.SMARTCARD_TO_ASSOCIATE_LOADED ]: this.handleSmartcardToAssociateLoaded,
            [ ActionTypes.SMARTCARD_REPLACEMENT_REQUEST_SUMMARY_FAILED]: this.handleReplacementRequestSummaryFailed,
            [ ActionTypes.SMARTCARD_REPLACEMENT_REQUEST_SUMMARY_LOADED]: this.handleReplacementRequestSummaryLoaded,
            [ ActionTypes.LOGGED_OUT ]: this.resetState
        };
    }

    handlePropertyChange (name, key) {
        return (payload) => this.setState(this.state.set(name, payload[key || name]));
    }

    handleSmartcardLoaded ({ smartcard }) {
        this.setState(
            this.state.merge({
                smartcard,
                smartcardDirty: false,
                lastUpdated: trainTime(),
                smartcardFailure: null,
                smartcardFailureNotified: false,
            })
        );
    }

    handleDirtySmartcard () {
        this.setState(this.state.set('smartcardDirty', true));
    }

    handleSmartcardFailed ({ error }) {
        this.setState(this.state.set('smartcardFailure', error));
    }

    handleSmartcardFailureNotified () {
        this.setState(this.state.set('smartcardFailureNotified', true));
    }

    resetState () {
        this.setState(this.getInitialState());
    }

    get activatedSmartcardsCookie () {
        const value = CookieHelper.get(CookieKeys.CONFIRMED_SMARTCARDS) || [];
        return new Set(value);
    }

    set activatedSmartcardsCookie (value) {
        const expires = moment().add(1, 'year').format();
        CookieHelper.set(CookieKeys.CONFIRMED_SMARTCARDS, value.toJS(), { expires });
    }

    handleSmartcardActivationStarted () {
        this.setState(this.state.set('smartcardActivating', true));
    }

    handleManualActivation () {
        const cardRef = this.state.getIn(['smartcard', 'reference']);
        const activatedCards = this.state.get('activatedSmartcards').add(cardRef);

        this.setState(this.state.set('activatedSmartcards', activatedCards).set('smartcardActivating', false));
        this.activatedSmartcardsCookie = activatedCards;
    }

    handleSmartcardActivationFailure () {
        this.setState(this.state.set('smartcardActivating', false));
    }

    handlePaperTransfer () {
        const smartcard = this.state.get('smartcard');
        const updated = smartcard.set('cardStatus', SmartcardStatus.AWAITING_FULFILMENT);
        this.setState(this.state.set('smartcard', updated));
    }

    handleAssociatedAccountsLoaded ({ accounts }) {
        this.setState(this.state.set('associatedAccounts', accounts));
    }

    handleAssociatedSmartcardsLoaded ({ smartcards }) {
        this.setState(this.state.set('associatedSmartcards', smartcards));
    }

    handleSmartcardToAssociateLoaded ({ smartcard }) {
        this.setState(this.state.set('smartcardToAssociate', smartcard));
    }

    handleReplacementRequestSummaryFailed () {
        this.setState(this.state.set('requestSummary', null));
    }

    handleReplacementRequestSummaryLoaded ({ requestSummary }) {
        this.setState(this.state.set('requestSummary', requestSummary));
    }

    getCurrent() {
        return this.get('smartcard');
    }

    shouldGetSmartcard (time = 5, timeUnits = 'minutes') {
        const lastUpdated = this.get('lastUpdated');

        return !this.get('smartcard')
            || this.get('smartcardDirty')
            || lastUpdated && lastUpdated.isBefore(trainTime().subtract(time, timeUnits));
    }

    isReplacementPending () {
        const smartcard = this.state.get('smartcard');
        return smartcard && smartcard.replacementInProgress;
    }

    canAutoDelayRepay () {
        const smartcard = this.state.get('smartcard');
        return smartcard && (smartcard.isActive() || this.isReplacementPending());
    }

    getAssociatedSmartcards () {
        return this.get('associatedSmartcards');
    }

}

 const storeInstance = new SmartcardStore();

/**
 * Hook to access the  smartcard
 * @returns {Smartcard}
 */
export const useSmartcard = debugHook(function _useSmartcard() {
    const state = useStoreState(storeInstance);
    return state ? {
        lastUpdated: state.get('lastUpdated'),
        smartcard: state.get('smartcard'),
        smartcardDirty: state.get('smartcardDirty'),
        smartcardFailure: state.get('smartcardFailure'),
        smartcardFailureNotified: state.get('smartcardFailureNotified'),
        smartcards: state.get('smartcards'),
        activatedSmartcards: state.get('activatedSmartcards'),
        associatedAccounts: state.get('associatedAccounts'),
        associatedSmartcards: state.get('associatedSmartcards'),
        smartcardToAssociate: state.get('smartcardToAssociate'),
        requestSummary: state.get('requestSummary'),
    } : {};
}, 'useSmartcard');

export default storeInstance;
